import type { ApiAgreementListingRequestBody } from '@/types/agreements';
import { queryKeys } from '@/utils/constants';

const MODULE_KEY = 'agreements';

/** Query Key Getters */

/**
 * Cache key for the paginated agreements list (`getAgreementsList` API).
 * @see ApiAgreementListingRequestBody
 */
export const getAgreementsListQueryKey = (
  teamTwrn?: string | null,
  requestBody?: ApiAgreementListingRequestBody | null,
) => {
  const key: unknown[] = [MODULE_KEY, queryKeys.GET_AGREEMENTS_LIST];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (requestBody !== undefined && requestBody !== null) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementsListQueryKey] requestBody was provided without teamTwrn. teamTwrn is required when requestBody is passed.',
        {
          requestBody,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(requestBody);
  }

  return key;
};

/**
 * Cache key for a single agreement's details (`getAgreementDetails` API).
 */
export const getAgreementDetailsQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_AGREEMENT_DETAILS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementDetailsQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  return key;
};

/**
 * Cache key for a single contract template (`getContractTemplate` API).
 */
export const getContractTemplateQueryKey = (
  teamTwrn?: string | null,
  contractTemplateTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_CONTRACT_TEMPLATE];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (contractTemplateTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getContractTemplateQueryKey] contractTemplateTwrn was provided without teamTwrn. teamTwrn is required when contractTemplateTwrn is passed.',
        {
          contractTemplateTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(contractTemplateTwrn);
  }

  return key;
};

/**
 * Cache key for a single athlete's contract (`getAthleteContract` API).
 * Shared by ActionsCell (lazy refetch on click) and AgreementDrawer (query-driven) so both
 * read/invalidate the same cache entry.
 */
export const getAthleteContractQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
  agreementAthleteTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_ATHLETE_CONTRACT];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteContractQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  if (agreementAthleteTwrn) {
    if (!agreementTwrn) {
      console.warn(
        '[getAthleteContractQueryKey] agreementAthleteTwrn was provided without agreementTwrn. agreementTwrn is required when agreementAthleteTwrn is passed.',
        {
          agreementAthleteTwrn,
          agreementTwrn,
        },
      );
      return key;
    }
    key.push(agreementAthleteTwrn);
  }

  return key;
};

/**
 * Cache key for athletes on an agreement (`getAgreementAthletes` API).
 *
 * Owns the stable prefix
 * `['agreements', 'getAgreementAthletes', <teamTwrn>, <agreementTwrn>]`.
 * View-specific trailing params (e.g. `{ limit: 5 }` on the agreement overview)
 * are appended by the read site after this prefix. Invalidations only ever need
 * the prefix, so pass fewer args to invalidate a broader slice of the cache.
 */
export const getAgreementAthletesQueryKey = (
  teamTwrn?: string | null,
  agreementTwrn?: string | null,
) => {
  const key: (string | null)[] = [MODULE_KEY, queryKeys.GET_AGREEMENT_ATHLETES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (agreementTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAgreementAthletesQueryKey] agreementTwrn was provided without teamTwrn. teamTwrn is required when agreementTwrn is passed.',
        {
          agreementTwrn,
          teamTwrn,
        },
      );
      return key;
    }
    key.push(agreementTwrn);
  }

  return key;
};
